import React, { useRef, useEffect, useMemo } from 'react';
import Globe from 'react-globe.gl';
import { motion } from 'framer-motion';
import { Radio, Star } from 'lucide-react';

// --- DATA: Locations ---
const locations = [
  { id: 1, name: "Pune (HQ)", lat: 18.5204, lng: 73.8567, color: "#ef4444", size: 1.5 },
  { id: 2, name: "South Africa", lat: -30.5595, lng: 22.9375, color: "#3b82f6", size: 0.8 },
  { id: 3, name: "Bhutan", lat: 27.5142, lng: 90.4336, color: "#3b82f6", size: 0.8 },
  { id: 4, name: "Manipur", lat: 24.6637, lng: 93.9063, color: "#06b6d4", size: 0.5 },
  { id: 5, name: "Assam", lat: 26.2006, lng: 92.9376, color: "#06b6d4", size: 0.5 },
  { id: 6, name: "Tripura", lat: 23.9408, lng: 91.9882, color: "#06b6d4", size: 0.5 },
  { id: 7, name: "Arunachal", lat: 28.2180, lng: 94.7278, color: "#06b6d4", size: 0.5 },
  { id: 8, name: "Punjab", lat: 31.1471, lng: 75.3412, color: "#06b6d4", size: 0.5 },
  { id: 9, name: "Rajasthan", lat: 27.0238, lng: 74.2179, color: "#06b6d4", size: 0.5 },
  { id: 10, name: "Delhi", lat: 28.7041, lng: 77.1025, color: "#06b6d4", size: 0.5 },
  { id: 11, name: "Haryana", lat: 29.0588, lng: 76.0856, color: "#06b6d4", size: 0.5 },
  { id: 12, name: "Uttar Pradesh", lat: 26.8467, lng: 80.9462, color: "#06b6d4", size: 0.5 },
];

const ThreeDGlobe = () => {
  const globeEl = useRef();
  
  // --- Create Random Golden Stars ---
  const goldenStars = useMemo(() => {
    return [...Array(70)].map((_, i) => ({
      left: `${Math.random() * 100}%`,
      top: `${Math.random() * 100}%`,
      size: Math.random() * 3 + 1,
      delay: Math.random() * 3,
      opacity: Math.random() * 0.7 + 0.3
    }));
  }, []);

  // --- Connecting Lines Logic ---
  const arcsData = useMemo(() => {
    const hq = locations.find(l => l.id === 1);
    return locations
      .filter(l => l.id !== 1)
      .map(loc => ({
        startLat: hq.lat,
        startLng: hq.lng,
        endLat: loc.lat,
        endLng: loc.lng,
        color: ['rgba(59, 130, 246, 0.5)', 'rgba(6, 182, 212, 0.8)'] 
      }));
  }, []);

  // --- Auto Rotate Setup (Moving Effect) ---
  useEffect(() => {
    if (globeEl.current) {
      // Isse Globe ghumna shuru karega
      globeEl.current.controls().autoRotate = true;
      globeEl.current.controls().autoRotateSpeed = 1.2; // Speed badha di hai (Normal 0.5 hoti hai)
      
      // Starting Camera Position
      globeEl.current.pointOfView({ lat: 22, lng: 70, altitude: 2.2 });
    }
  }, []);

  return (
    <section className="w-full h-[850px] bg-[#020617] relative overflow-hidden flex flex-col items-center justify-center border-t border-slate-800">
      
      {/* --- GOLDEN STARS LAYER (Twinkling) --- */}
      <div className="absolute inset-0 pointer-events-none">
        {goldenStars.map((star, i) => (
          <div 
            key={i}
            className="absolute rounded-full bg-yellow-300 shadow-[0_0_6px_rgba(253,224,71,0.6)] animate-pulse"
            style={{
              left: star.left,
              top: star.top,
              width: `${star.size}px`,
              height: `${star.size}px`,
              opacity: star.opacity,
              animationDuration: `${1.5 + star.delay}s` // Twinkle speed
            }}
          />
        ))}
      </div>

      {/* --- Blue Glow Effect --- */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/10 blur-[100px] rounded-full pointer-events-none" />

      {/* --- Heading Section --- */}
      <div className="absolute top-10 z-10 text-center pointer-events-none px-4">
        <motion.div 
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900/60 backdrop-blur-md border border-yellow-500/40 text-yellow-400 text-xs font-bold uppercase tracking-widest mb-4 shadow-sm"
        >
            <Star size={14} className="fill-yellow-400 text-yellow-400" />
            <span>Live Global Network</span>
        </motion.div>
        
        <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight">
          Connecting <br className="md:hidden" />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">The World.</span>
        </h2>
      </div>

      {/* --- 3D MOVING GLOBE --- */}
      <div className="cursor-grab active:cursor-grabbing w-full h-full relative z-10">
        <Globe
          ref={globeEl}
          backgroundColor="rgba(0,0,0,0)" // Transparent background
          
          // Texture: Bright Day-Time Earth (White Theme Style)
          globeImageUrl="//unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
          
          // Atmosphere
          atmosphereColor="#bae6fd" 
          atmosphereAltitude={0.15}
          
          // Lines (Data Connections)
          arcsData={arcsData}
          arcColor={'color'}
          arcDashLength={0.4}
          arcDashGap={0.6}
          arcDashAnimateTime={2000} // Lines move fast
          arcStroke={0.5}

          // Markers
          labelsData={locations}
          labelLat={d => d.lat}
          labelLng={d => d.lng}
          labelText={d => d.name}
          labelSize={d => d.id === 1 ? 1.5 : 1} 
          labelDotRadius={d => d.size}
          labelColor={d => d.color}
          labelResolution={2}
          labelAltitude={0.01}
          
          // Rings (Ripples)
          ringsData={locations}
          ringColor={() => (t) => `rgba(255,255,255,${1-t})`}
          ringMaxRadius={d => d.id === 1 ? 6 : 3}
          ringPropagationSpeed={2}
          ringRepeatPeriod={800}
        />
      </div>

      {/* --- Bottom Stats Box --- */}
      <div className="absolute bottom-10 w-full px-6 flex justify-center gap-4 md:justify-between max-w-5xl pointer-events-none z-20">
         <div className="bg-slate-900/50 backdrop-blur-md border border-white/10 p-4 rounded-xl text-center md:text-left shadow-lg">
            <p className="text-slate-400 text-[10px] uppercase tracking-wider font-semibold">Total Regions</p>
            <p className="text-xl md:text-2xl font-bold text-white">12+</p>
         </div>
         <div className="bg-slate-900/50 backdrop-blur-md border border-white/10 p-4 rounded-xl text-center md:text-left shadow-lg">
             <p className="text-slate-400 text-[10px] uppercase tracking-wider font-semibold">Headquarters</p>
             <p className="text-xl md:text-2xl font-bold text-white">Pune, India</p>
         </div>
      </div>

    </section>
  );
};

export default ThreeDGlobe;